import React, { useState, useMemo, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import { Button } from '@/ui';
import { useField } from 'react-final-form';
import { FieldArrayRenderProps } from 'react-final-form-arrays';
import CustomVariableItemFinal from './CustomVariableItemFinal';
import VerifyModalFinal from './VerifyModalFinal';
import { extractUDKeys, CustomVariable } from './utils';

interface CustomVariablesFormFinalProps {
  fields: FieldArrayRenderProps<any, HTMLElement>['fields'];
  customVarFlag: string;
  meta: FieldArrayRenderProps<any, HTMLElement>['meta'];
  formValues?: {
    container?: { content?: string };
    layout?: { content?: string };
  };
  showVerify?: boolean;
}

const CustomVariablesFormFinal = React.memo(function CustomVariablesFormFinal({
  fields,
  customVarFlag,
  meta,
  formValues = {},
  showVerify = false,
}: CustomVariablesFormFinalProps) {
  const [modal, setModal] = useState(false);
  const toggleModal = useCallback(() => setModal((prev) => !prev), []);

  const {
    input: { value: customVariables },
  } = useField(fields.name, { subscription: { value: true } });
  const safeCustomVariables: CustomVariable[] = Array.isArray(customVariables) ? customVariables : [];

  const addCustomVariable = useCallback(() => {
    fields.push({
      key: customVarFlag,
    });
  }, [fields, customVarFlag]);

  const verifyCustomVariables = useMemo(() => {
    if (!showVerify)
      return { customVariablesNotInContainerLayout: [], containerMissingKeys: [], layoutMissingKeys: [] };

    const containerUDKeys = extractUDKeys(formValues?.container?.content || '');
    const layoutUDKeys = extractUDKeys(formValues?.layout?.content || '');

    const containerMissingKeys = containerUDKeys.filter(
      (containerUDKey) => !safeCustomVariables.some((customVariable) => customVariable.key === containerUDKey.key)
    );
    const layoutMissingKeys = layoutUDKeys.filter(
      (layoutUDKey) => !safeCustomVariables.some((customVariable) => customVariable.key === layoutUDKey.key)
    );

    const containerLayoutUDKeys = [...containerUDKeys, ...layoutUDKeys];

    const customVariablesNotInContainerLayout = safeCustomVariables.filter(
      (customVariable) =>
        !containerLayoutUDKeys.some((containerLayoutUDKey) => containerLayoutUDKey.key === customVariable.key)
    );

    return { customVariablesNotInContainerLayout, containerMissingKeys, layoutMissingKeys };
  }, [formValues?.container?.content, formValues?.layout?.content, showVerify]);

  const { customVariablesNotInContainerLayout, containerMissingKeys, layoutMissingKeys } = verifyCustomVariables;

  const customVariableItems = useMemo(() => {
    return fields.map((name, index) => (
      <CustomVariableItemFinal
        key={name}
        customVariablesItem={name}
        index={index}
        fields={fields}
        customVarFlag={customVarFlag}
      />
    ));
  }, [fields, customVarFlag]);

  const fieldsCount = safeCustomVariables.length;
  const { touched, error } = meta || {};

  return (
    <div>
      <Stack direction='row' spacing={2} sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <div>
          <Typography variant='h6'>Custom Variables</Typography>
          {fieldsCount ? (
            <Typography variant='subtitle2' color='text.secondary'>
              Total {fieldsCount}
            </Typography>
          ) : null}
        </div>
        <Stack direction='row' spacing={1}>
          {showVerify && (
            <Button color='success' onClick={toggleModal}>
              Verify Custom Variables
            </Button>
          )}
          <Button color='success' onClick={addCustomVariable} aria-label='Add custom variable'>
            <Add fontSize='small' />
          </Button>
        </Stack>
      </Stack>
      {touched && error && (typeof error === 'string' ? <span style={{ color: '#f86c6b' }}>{error}</span> : null)}

      {customVariableItems}

      <VerifyModalFinal
        isOpen={modal}
        toggle={toggleModal}
        customVarFlag={customVarFlag}
        layoutMissingKeys={layoutMissingKeys}
        containerMissingKeys={containerMissingKeys}
        customVariablesNotInContainerLayout={customVariablesNotInContainerLayout}
        fields={fields}
        customVariables={safeCustomVariables}
      />
    </div>
  );
});

export default CustomVariablesFormFinal;
